import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BannerFuentes } from "@/components/BannerFuentes";

// Datos verificados Bloque D · Mesas 900K (SPEC MASTER v4 + memoria canónica padrón)
// Fuentes: SAAE/JFT 6-7 mayo 2026 + sesión Congreso Comisión Constitución 5-may + ONPE
const INV = {
  totalMesas: 4_703,
  electores: 1_109_876, // canon memoria padrón EG2026
  sinCCPP: 3,
  crecimiento: { e2021: 3_108, e2026: 4_703, deltaPct: 51.32 },
};

const ARGUMENTO = {
  leyUsadaPorONPE: "Ley 28.440",
  ambitoRealLey: "Elección de alcaldes y regidores de Municipalidades de CC.PP. únicamente",
  ambitoQueSeCubrioIncorrectamente: "Elecciones Generales (presidencial + congresales)",
  conclusion: "Las 4,703 actas 900K en EG2026 carecerían de base legal aplicable",
  fuente: "Elías Jesús Silva Huallanca (CAC 6233) · Comisión Constitución del Congreso 5-may-2026",
  estado: "🟡 Hipótesis técnica con argumento jurídico documentado",
};

const DISTRIBUCION = [
  { iniciales: "RS",  candidato: "Roberto Sánchez Palomino",     votos: 249_483, pct900K: 41.84, pctNacional: 12.03 },
  { iniciales: "KFH", candidato: "Keiko Fujimori Higuchi",        votos:  97_625, pct900K: 16.37, pctNacional: 17.18 },
  { iniciales: "RLA", candidato: "Rafael López Aliaga Cazorla",   votos:  15_310, pct900K:  2.57, pctNacional: 11.90 },
];

const SIN_CCPP = [
  { mesa: 900994, ubigeo: "050310", localidad: "Ayacucho · Huanta · Canayre",              electores: 160, nota: "VRAEM — intervención directa" },
  { mesa: 903556, ubigeo: "150903", localidad: "Loreto · Putumayo · Tte. Manuel Clavero",  electores: 283, nota: "Frontera Colombia" },
  { mesa: 903557, ubigeo: "150903", localidad: "Loreto · Putumayo · (mismo local)",         electores: 286, nota: "Frontera Colombia" },
];

const TOP_CRECIMIENTO = [
  { mesa: 902302, ccpp: "Pachabamba",   e2021:  86, e2026: 295, delta: 243.0 },
  { mesa: 900331, ccpp: "Yanarumi",     e2021:  85, e2026: 248, delta: 191.8 },
  { mesa: 900682, ccpp: "Socco",        e2021:  87, e2026: 250, delta: 187.4 },
  { mesa: 900036, ccpp: "Imacita",      e2021:  83, e2026: 234, delta: 181.9 },
  { mesa: 900560, ccpp: "Pariashpampa", e2021:  87, e2026: 238, delta: 173.6 },
];

const ANOMALIAS = {
  concentracion90: { mesas: 17, breakdown: "15 JP/RSP + 2 FP/KFH" },
  nulosBlancos: { pct900K: 27.8, pctNo900K: 16.4, delta: 11.4 },
  declaracionMunante: ">400 actas 900K firmadas por el mismo miembro de mesa · misma letra en presidente/secretario/vocal · nombres ficticios de partidos inexistentes",
};

const PENDIENTES = [
  "Procedimiento ONPE 20-ago-2025 que origina la numeración 900K — NO publicado. Solicitud formal en curso Art. 96 Constitución.",
  "PDFs de las 4,703 actas 900K en S3 ONPE (URLs expiradas — refrescar M01 SAAE).",
  "Análisis grafotécnico de las 400+ actas con firma única denunciadas por Muñante.",
  "Cruce con padrones municipales de CC.PP. (Ley 27972).",
  "Transferencias MEF a CC.PP. vinculadas con mesas 900K.",
];

const fmt = (n: number) => new Intl.NumberFormat("es-PE").format(n);

type CruceResp = {
  resumen: {
    totalDistritosCon900K: number;
    totalMesas900K: number;
    severidad: Record<string, { distritos: number; mesas900k: number }>;
  };
  distritos: Array<{
    ubigeoReniec: string;
    departamento: string;
    provincia: string;
    distrito: string;
    mesas900K: number;
    electores900K: number;
    electoresInicio: number | null;
    electoresFin: number | null;
    pctCrecimiento: number | null;
    severidad: string;
    score: number;
  }>;
};
type SerieResp = {
  serie: Array<{
    proceso: string;
    mesas900k: number;
    confiabilidadElectores: string;
    pctParticipacion: number;
    promElectores: number | null;
  }>;
};

function CrecimientoSection() {
  const cruce = useQuery({
    queryKey: ["cruce-900k"],
    queryFn: async () => (await api.get<CruceResp>("/mesas-900k/cruce-crecimiento?sort=mesas_x_pct&limit=30")).data,
  });
  const serie = useQuery({
    queryKey: ["serie-900k"],
    queryFn: async () => (await api.get<SerieResp>("/mesas-900k/serie-historica")).data,
  });

  return (
    <>
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Serie histórica · mesas 900K por proceso electoral
        </h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="px-3 py-3">Proceso</th>
                <th className="px-3 text-right">Mesas 900K</th>
                <th className="px-3 text-right">Δ vs proceso anterior</th>
                <th className="px-3 text-right">% participación</th>
                <th className="px-3">Confiabilidad cifra</th>
              </tr>
            </thead>
            <tbody>
              {serie.data?.serie.map((s, i, arr) => {
                const prev = arr[i - 1];
                const delta = prev ? s.mesas900k - prev.mesas900k : 0;
                const pctDelta = prev && prev.mesas900k > 0 ? ((delta / prev.mesas900k) * 100) : 0;
                return (
                  <tr key={s.proceso} className="border-b border-ink-700/30">
                    <td className="px-3 py-2 font-mono text-ink-100">{s.proceso}</td>
                    <td className="px-3 text-right font-mono tabular-nums text-ink-100">{fmt(s.mesas900k)}</td>
                    <td className="px-3 text-right font-mono tabular-nums">
                      {prev ? (
                        <span className={pctDelta >= 50 ? "text-sem-contradiccion" : pctDelta >= 20 ? "text-sem-hipotesis" : "text-ink-200"}>
                          +{fmt(delta)} ({pctDelta >= 0 ? "+" : ""}{pctDelta.toFixed(1)}%)
                        </span>
                      ) : "—"}
                    </td>
                    <td className="px-3 text-right text-ink-200">
                      {s.pctParticipacion > 0 ? `${s.pctParticipacion.toFixed(2)}%` : "—"}
                      {s.pctParticipacion > 100 && (
                        <span className="ml-1 text-sem-contradiccion text-xs">⚠ &gt;100%</span>
                      )}
                    </td>
                    <td className="px-3 text-xs">
                      {s.confiabilidadElectores === "canon" ? (
                        <span className="chip-probado">canon mxm_master</span>
                      ) : (
                        <span className="chip-vacio" title="electoresTotal viene inflado por la vista origen">⚠ vista inflada</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-ink-400">
          EG2006 reporta <strong className="text-sem-contradiccion">101.51% de participación</strong> en sus 495 mesas 900K — anomalía irreductible.
          De 2006 a 2026: <strong className="text-sem-contradiccion">+850% mesas 900K</strong> en 20 años.
        </p>
      </section>

      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Cruce: distritos con mesas 900K × crecimiento del padrón 2011-2026
        </h2>
        {cruce.data?.resumen && (
          <div className="mt-3 grid gap-3 md:grid-cols-4">
            <KpiCard label="Distritos con 900K" value={fmt(cruce.data.resumen.totalDistritosCon900K)} hint="de 1,890 distritos canon" />
            <KpiCard label="Mesas 900K (canon)" value={fmt(cruce.data.resumen.totalMesas900K)} />
            <KpiCard
              label="Con crecimiento EXTREMO"
              value={`${cruce.data.resumen.severidad.extremo?.distritos ?? 0} distritos`}
              hint={`${fmt(cruce.data.resumen.severidad.extremo?.mesas900k ?? 0)} mesas 900K`}
            />
            <KpiCard
              label="Crecimiento NORMAL"
              value={`${cruce.data.resumen.severidad.normal?.distritos ?? 0} distritos`}
              hint={`${fmt(cruce.data.resumen.severidad.normal?.mesas900k ?? 0)} mesas 900K`}
            />
          </div>
        )}

        <div className="mt-4 rounded-lg border border-sem-vacio/40 bg-ink-900/40 p-4 text-sm text-ink-200">
          <strong className="text-ink-50">⚫ Lectura editorial requerida:</strong> Que un distrito tenga muchas mesas 900K
          Y crecimiento extremo del padrón <em>no prueba irregularidad</em>. Distritos rurales con minería formal o
          frontera (Madre de Dios, Loreto), zonas VRAEM (Río Tambo, Echarate), distritos creados por ley
          (Constitución, Samugari) tienen explicaciones legítimas. La señal compuesta solo identifica candidatos a
          revisión — la verificación requiere INEI/RENIEC vitales.
        </div>

        <h3 className="mt-6 text-sm font-semibold text-ink-100">Top 30 distritos por (mesas 900K × % crecimiento padrón)</h3>
        <div className="mt-2 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="px-3 py-3">Distrito</th>
                <th className="px-3 text-right">Mesas 900K</th>
                <th className="px-3 text-right">Padrón 2011</th>
                <th className="px-3 text-right">Padrón 2026</th>
                <th className="px-3 text-right">% crec.</th>
                <th className="px-3">Severidad</th>
              </tr>
            </thead>
            <tbody>
              {cruce.data?.distritos.slice(0, 30).map((d) => (
                <tr key={d.ubigeoReniec} className="border-b border-ink-700/30 hover:bg-ink-800/50">
                  <td className="px-3 py-2">
                    <div className="text-ink-50">{d.distrito}</div>
                    <div className="text-xs text-ink-400">{d.departamento} · {d.provincia}</div>
                  </td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-100">{d.mesas900K}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-300">{d.electoresInicio !== null ? fmt(d.electoresInicio) : "—"}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-100">{d.electoresFin !== null ? fmt(d.electoresFin) : "—"}</td>
                  <td className="px-3 text-right font-mono tabular-nums">
                    {d.pctCrecimiento !== null ? (
                      <span className={d.pctCrecimiento >= 50 ? "text-sem-contradiccion" : d.pctCrecimiento >= 30 ? "text-sem-hipotesis" : "text-ink-200"}>
                        {d.pctCrecimiento >= 0 ? "+" : ""}{d.pctCrecimiento.toFixed(1)}%
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-3"><span className={chipSevLocal(d.severidad)}>{d.severidad}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-3 text-xs text-ink-400">
          Ver universo completo en{" "}
          <Link to="/laboratorio/crecimiento-padron" className="text-gold-300 hover:underline">/laboratorio/crecimiento-padron</Link>
        </div>
      </section>
    </>
  );
}

const chipSevLocal = (s: string) =>
  ({
    extremo: "chip-contradiccion",
    alto: "chip-hipotesis",
    normal: "chip-probado",
    decrecimiento: "chip-vacio",
    distrito_nuevo: "chip-reconocido",
    sin_data: "chip-vacio",
  } as Record<string, string>)[s] ?? "chip-vacio";

export function Mesas900KPage() {
  return (
    <div className="container-prose py-12 md:py-16">
      <div className="flex flex-wrap items-center gap-2">
        <span className="chip-hipotesis">🟡 Investigación con hipótesis técnica</span>
        <span className="chip-reconocido">🔵 Reconocido en sesión Congreso 5-may</span>
      </div>

      <h1 className="mt-6 font-serif text-4xl text-ink-50 md:text-5xl">Mesas 900K</h1>
      <p className="mt-3 text-xs uppercase tracking-widest text-gold-400/80">
        Investigación CCPP · Centros poblados · EG2026
      </p>
      <p className="mt-4 max-w-3xl text-lg text-ink-200">
        Las mesas con numeración a partir del 900,000 son una categoría operativa creada por ONPE
        para concentrar votación en centros poblados rurales. En EG2026 son{" "}
        <strong className="text-ink-50">{fmt(INV.totalMesas)} mesas</strong> con{" "}
        <strong className="text-ink-50">{fmt(INV.electores)} electores</strong> asignados —
        equivalente a una circunscripción mediana.
      </p>

      <BannerFuentes pagina="/mesas-900k" />

      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Universo 900K en EG2026</h2>
        <div className="mt-3 grid gap-3 md:grid-cols-4">
          <KpiCard label="Mesas 900K" value={fmt(INV.totalMesas)} hint="del total de 92,766 mesas EG2026" />
          <KpiCard label="Electores asignados" value={fmt(INV.electores)} hint="canon mxm_master ONPE" />
          <KpiCard label="Mesas SIN CCPP" value={String(INV.sinCCPP)} hint="anomalía estructural irreductible" />
          <KpiCard label="Crecimiento vs 2021" value={`+${INV.crecimiento.deltaPct.toFixed(1)}%`} hint={`de ${fmt(INV.crecimiento.e2021)} a ${fmt(INV.crecimiento.e2026)} mesas`} />
        </div>
      </section>

      {/* Argumento legal central */}
      <section className="mt-10 rounded-lg border border-sem-hipotesis/30 bg-sem-hipotesis/5 p-5">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Argumento legal central</h2>
        <h3 className="mt-2 font-serif text-xl text-ink-50">{ARGUMENTO.conclusion}</h3>
        <div className="mt-3 grid gap-2 text-sm md:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-400">Ley invocada por ONPE</div>
            <div className="text-ink-200">{ARGUMENTO.leyUsadaPorONPE}</div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-ink-400">Ámbito real de la ley</div>
            <div className="text-ink-200">{ARGUMENTO.ambitoRealLey}</div>
          </div>
          <div className="md:col-span-2">
            <div className="text-xs uppercase tracking-widest text-ink-400">Ámbito incorrectamente cubierto</div>
            <div className="text-ink-200">{ARGUMENTO.ambitoQueSeCubrioIncorrectamente}</div>
          </div>
        </div>
        <p className="mt-3 text-xs text-ink-400">
          <strong className="text-ink-200">Fuente:</strong> {ARGUMENTO.fuente}
        </p>
        <p className="mt-1 text-xs">
          <span className="chip-hipotesis">{ARGUMENTO.estado}</span>
        </p>
      </section>

      {/* Distribución política */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Distribución de votos en mesas 900K — top divergencias vs nacional
        </h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="px-3 py-3">Candidato</th>
                <th className="px-3 text-right">Votos en 900K</th>
                <th className="px-3 text-right">% en 900K</th>
                <th className="px-3 text-right">% nacional</th>
                <th className="px-3 text-right">Δ (pp)</th>
              </tr>
            </thead>
            <tbody>
              {DISTRIBUCION.map((d) => {
                const delta = d.pct900K - d.pctNacional;
                const sign = delta > 0 ? "+" : "";
                return (
                  <tr key={d.iniciales} className="border-b border-ink-700/30">
                    <td className="px-3 py-2">
                      <span className="mr-2 inline-block rounded bg-gold-500/15 px-1.5 py-0.5 text-xs text-gold-300 font-mono">
                        {d.iniciales}
                      </span>
                      <span className="text-ink-100">{d.candidato}</span>
                    </td>
                    <td className="px-3 text-right font-mono tabular-nums text-ink-100">{fmt(d.votos)}</td>
                    <td className="px-3 text-right font-mono tabular-nums text-gold-300">{d.pct900K.toFixed(2)}%</td>
                    <td className="px-3 text-right font-mono tabular-nums text-ink-200">{d.pctNacional.toFixed(2)}%</td>
                    <td className={`px-3 text-right font-mono tabular-nums ${delta > 10 ? "text-sem-contradiccion" : delta < -5 ? "text-sem-hipotesis" : "text-ink-200"}`}>
                      {sign}{delta.toFixed(2)}pp
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="mt-2 text-xs text-ink-400">
          Sánchez (RS) obtuvo <strong className="text-ink-100">+29.81pp</strong> más en mesas 900K
          que su promedio nacional. Inversión simétrica para RLA y KFH respecto a sus promedios.
        </p>
      </section>

      {/* Mesas sin CCPP */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Anomalía irreductible: {INV.sinCCPP} mesas 900K SIN centro poblado asignado
        </h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-sem-contradiccion/30 bg-sem-contradiccion/5">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/40 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="px-3 py-3">Mesa</th>
                <th className="px-3">Ubigeo</th>
                <th className="px-3">Localidad</th>
                <th className="px-3 text-right">Electores</th>
                <th className="px-3">Nota</th>
              </tr>
            </thead>
            <tbody>
              {SIN_CCPP.map((m) => (
                <tr key={m.mesa} className="border-b border-ink-700/20">
                  <td className="px-3 py-2 font-mono text-ink-100">{m.mesa}</td>
                  <td className="px-3 font-mono text-ink-300">{m.ubigeo}</td>
                  <td className="px-3 text-ink-100">{m.localidad}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-100">{m.electores}</td>
                  <td className="px-3 text-xs text-ink-300">{m.nota}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Top crecimiento */}
      <section className="mt-10">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          Top crecimiento de electores por mesa (2026 vs 2021)
        </h2>
        <div className="mt-3 overflow-x-auto rounded-lg border border-ink-700/60 bg-ink-900/50">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-ink-700/60 text-left text-xs uppercase tracking-wide text-ink-300">
                <th className="px-3 py-3">Mesa</th>
                <th className="px-3">CCPP</th>
                <th className="px-3 text-right">Electores 2021</th>
                <th className="px-3 text-right">Electores 2026</th>
                <th className="px-3 text-right">Δ (%)</th>
              </tr>
            </thead>
            <tbody>
              {TOP_CRECIMIENTO.map((m) => (
                <tr key={m.mesa} className="border-b border-ink-700/30">
                  <td className="px-3 py-2 font-mono text-ink-100">{m.mesa}</td>
                  <td className="px-3 text-ink-100">{m.ccpp}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-200">{m.e2021}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-ink-100">{m.e2026}</td>
                  <td className="px-3 text-right font-mono tabular-nums text-sem-contradiccion">+{m.delta.toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* Anomalías estadísticas */}
      <section className="mt-10 rounded-lg border border-ink-700/60 bg-ink-900/40 p-5">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">Anomalías estadísticas</h2>
        <ul className="mt-3 space-y-2 text-sm">
          <li>
            <span className="chip-hipotesis">🟡</span>{" "}
            <strong className="text-ink-100">{ANOMALIAS.concentracion90.mesas} mesas con concentración ≥ 90%</strong>{" "}
            <span className="text-ink-300">({ANOMALIAS.concentracion90.breakdown})</span>
          </li>
          <li>
            <span className="chip-hipotesis">🟡</span>{" "}
            <strong className="text-ink-100">Tasa nulos+blancos en 900K: {ANOMALIAS.nulosBlancos.pct900K}%</strong>{" "}
            <span className="text-ink-300">vs {ANOMALIAS.nulosBlancos.pctNo900K}% en no-900K (Δ = +{ANOMALIAS.nulosBlancos.delta}pp)</span>
          </li>
          <li>
            <span className="chip-alegacion">🟠 Denuncia Muñante (RP)</span>
            <p className="mt-1 text-ink-200 text-sm">{ANOMALIAS.declaracionMunante}</p>
          </li>
        </ul>
      </section>

      <CrecimientoSection />

      {/* Pendientes */}
      <section className="mt-10 rounded-lg border border-sem-vacio/30 bg-ink-900/40 p-5">
        <h2 className="text-xs uppercase tracking-widest text-ink-400">
          ⚫ Pendientes documentales / de investigación
        </h2>
        <ol className="mt-3 list-decimal pl-5 space-y-1.5 text-sm text-ink-200">
          {PENDIENTES.map((p, i) => (
            <li key={i}>{p}</li>
          ))}
        </ol>
      </section>

      <p className="mt-8 text-xs text-ink-400">
        Fuentes primarias en{" "}
        <a href="/jne" className="text-gold-300 hover:underline">/jne</a> (Acuerdo Pleno + sesión
        Congreso 5-may) y <a href="/onpe" className="text-gold-300 hover:underline">/onpe</a>{" "}
        (Plan Operativo + auditoría M&amp;T).
      </p>
    </div>
  );
}

function KpiCard({ label, value, hint }: { label: string; value: string; hint?: ReactNode }) {
  return (
    <div className="rounded-lg border border-ink-700/60 bg-ink-900/60 p-4">
      <div className="text-xs uppercase tracking-widest text-ink-400">{label}</div>
      <div className="mt-1 font-mono text-xl tabular-nums text-ink-50">{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-400">{hint}</div>}
    </div>
  );
}
